import { useCallback, useEffect, useState } from 'react';

import api, { FetchError } from '../../api/api';
import { useAppContext } from '../../AppContext';
import { StudyParticipant } from '../../models/StudyParticipant';

const RETRY_DELAYS_MS = [1000, 3000];

function shouldRetry(err: any): boolean {
  if (!err) return false;
  if (typeof err.statusCode === 'number') {
    return err.statusCode >= 500;
  }
  return true;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function useDeleteStudyParticipant(id: number) {
  const { logError } = useAppContext();
  const [data, setData] = useState<StudyParticipant | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<FetchError | undefined>();

  useEffect(
    () => void (error && logError('Delete Study Participant Error', error)),
    [error, logError]
  );

  const deleteStudyParticipant = useCallback(async (): Promise<StudyParticipant> => {
    const path = `study-participants/${id}`;

    setIsLoading(true);
    setError(undefined);

    let lastError: any;
    for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
      if (attempt > 0) {
        await sleep(RETRY_DELAYS_MS[attempt - 1]);
      }
      try {
        const { response } = api.delete(path, {});
        const result = await response;
        if (result.ok) {
          const json = await result.json();
          setData(json);
          setIsLoading(false);
          return json;
        }
        let errorBody: any;
        try {
          errorBody = await result.json();
        } catch {
          errorBody = {
            statusCode: result.status,
            message: result.statusText
          };
        }
        lastError = errorBody;
        if (!shouldRetry(errorBody)) break;
      } catch (err) {
        lastError = err;
        if (!shouldRetry(err)) break;
      }
    }

    setError(lastError);
    setIsLoading(false);
    throw lastError;
  }, [id]);

  return {
    deleteResult: data,
    deletingStudyParticipant: isLoading,
    deleteStudyParticipantError: error,
    deleteStudyParticipant
  };
}
